import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge, Collapse, Divider, Icon, IconButton, List, ListItemButton, ListItemIcon, ListItemText, ListSubheader, SwipeableDrawer, Tooltip, Typography } from '@mui/material';
import { useAccount, useIsAuthenticated } from '@azure/msal-react';

import { NavItem } from '../../models/navItem';
import { navigationService } from '../../services/navigation.service';
import { useUnreadNotificationsCount } from '../../../hooks/useUnreadNotificationsCount';

import logoSvg from './../../../assets/images/logo/ma_infraplannen_logo.svg';


const NavMain: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [collapseState, setCollapseState] = useState<Record<string, boolean>>({});
  const isAuthenticated = useIsAuthenticated();
  const [menuItems, setMenuItems] = useState<NavItem[]>([]);
  const account = useAccount();
  const navigate = useNavigate();
  const { count: unreadCount } = useUnreadNotificationsCount();

  const location = "main";

  const fetchMenuItems = async () => {
    const items = await navigationService.getNavigationItems(location);
    setMenuItems(items);
  };

  useEffect(() => {
    fetchMenuItems();
  }, [isAuthenticated, account]);

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const toggleCollapse = (name: string) => {
    setCollapseState((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  const onClickNavigate = (path: string) => {
    if (path.startsWith('http')) {
      window.open(path, '_blank', 'noopener,noreferrer');
    }
    else {
      navigate(path);
    }
    setOpen(false);
  };

  return (
    <div className="navigation nav-main" role="navigation" aria-label="Hoofd navigatie" >
      <IconButton onClick={toggleDrawer(true)} 
        aria-label="open menu"
        className="btn-main-menu"
        size="large">
        <Icon fontSize="inherit">menu</Icon>
      </IconButton>
      <div className="logo">
        <a href="/" aria-label="Ga naar de homepage van infraplannen.nl">
          <img src={logoSvg} className="main-logo" alt="infraplannen.nl" />
        </a>
      </div>

      <SwipeableDrawer open={open} onOpen={toggleDrawer(true)} onClose={toggleDrawer(false)}>
        <div className="navigation nav-main-open">
          <div className="nav-top">
            <div className="logo">
              <a href="/" aria-label="Ga naar de homepage van infraplannen.nl">
                <img src={logoSvg} className="main-logo" alt="infraplannen.nl" />
              </a>
            </div>
          </div>
          <div className="nav-items">
            <Divider />
            <List 
              sx={{ width: '100%', maxWidth: 360, bgcolor: 'background.paper' }}
              component="nav"
              aria-labelledby="nested-list-subheader"
              subheader={
                <ListSubheader component="div" id="nested-list-subheader">
                  {/* Menu subheader */}
                </ListSubheader>
              }>
              {menuItems.map(({ path, name, icon, children, component }, index) => (
                path === "" && name === "divider" ? ( // Render divider if configured
                  <Divider key={`divider-${index}`} />
                ) : component === 'title' ? (
                  <ListSubheader
                    key={`title-${index}`}
                    component="div"
                    disableSticky
                    sx={{
                      lineHeight: 1.6,
                      bgcolor: 'transparent',
                      px: 2,
                      pt: 0.5,
                      pb: 0,
                    }}
                  >
                    <Typography variant="overline" color="text.secondary">
                      {name}
                    </Typography>
                  </ListSubheader>
                ) : (
                  <React.Fragment key={path || `item-${index}`}>
                    {children && children.length > 0 ? (
                      <>
                        <ListItemButton onClick={() => toggleCollapse(name)}>
                          <ListItemIcon>
                            <Icon>{icon}</Icon>
                          </ListItemIcon>
                          <ListItemText primary={name} />
                          <Icon>{collapseState[name] ? "keyboard_arrow_up" : "keyboard_arrow_down"}</Icon>
                        </ListItemButton>
                        <Collapse in={collapseState[name]} timeout="auto" unmountOnExit>
                          <List component="div" disablePadding>
                            {children.map(({ path, name, icon }, childIndex) => (
                              <ListItemButton key={path || `child-${childIndex}`} sx={{ pl: 4 }} onClick={() => onClickNavigate(path)}>
                                <ListItemIcon>
                                  <Icon>{icon}</Icon>
                                </ListItemIcon>
                                <ListItemText primary={name} />
                              </ListItemButton>
                            ))}
                          </List>
                        </Collapse>
                      </>
                    ) : (
                      <ListItemButton onClick={() => onClickNavigate(path)}>
                        <ListItemIcon>
                          {path === '/berichtencentrum' ? (
                            <Badge badgeContent={unreadCount} color="warning" max={99} sx={{ '& .MuiBadge-badge': { top: -2, right: -2 } }}>
                              <Icon>{icon}</Icon>
                            </Badge>
                          ) : (
                            <Icon>{icon}</Icon>
                          )}
                        </ListItemIcon>
                        <ListItemText primary={name} />
                      </ListItemButton>
                    )}
                  </React.Fragment>
                )
              ))}

            </List>

            {/* Some menu examples 
              <ListItemButton onClick={ () => onClickCollapse() }>
                <ListItemIcon>
                  <Icon>business</Icon>
                </ListItemIcon>
                <ListItemText primary="Infraplannen" />
                {collapse ? <Icon>keyboard_arrow_up</Icon> : <Icon>keyboard_arrow_down</Icon>}
              </ListItemButton>
              <Collapse in={collapse} timeout="auto" unmountOnExit>
                <List component="div" disablePadding>
                  <ListItemButton sx={{ pl: 4 }} onClick={ () => onClickNavigate('/over') }>
                    <ListItemIcon>
                      <Icon>info</Icon>
                    </ListItemIcon>
                    <ListItemText primary="Over" />
                  </ListItemButton>
                  <ListItemButton sx={{ pl: 4 }} onClick={ () => onClickNavigate('/contact') }>
                    <ListItemIcon>
                      <Icon>chat_info</Icon>
                    </ListItemIcon>
                    <ListItemText primary="Contact" />
                  </ListItemButton>
                </List>
              </Collapse> */}

          </div>
        </div>
      </SwipeableDrawer>
    </div>
  );
}

export default NavMain;
